// La Loge : aperçus fabriqués dans le navigateur du studio avant l'envoi (docs/LOGE.md, « Original et aperçu »).
// Le client ne reçoit que ces aperçus : images réduites à PREVIEW_MAX_PX au plus, pages de PDF rendues par pdf.js,
// toutes marquées d'un filigrane discret en diagonale (« ACTE·5 · CONFIDENTIEL »), sauf la couverture du projet.
// Les originaux ne sortent qu'après déblocage.
//
// Le haut du fichier est pur (dimensions, pages, disposition du filigrane, découpage en morceaux) et se teste sous
// Node (tests/unit/logePreview.test.ts). Le canevas et pdf.js ne sont touchés que dans les fonctions du bas, appelées
// depuis World ; pdf.js n'est chargé qu'au premier PDF (import dynamique, morceau séparé).
import { CHUNK_BYTES, PREVIEW_MAX_PX, watermarkText, type LogeLang } from './loge';

/** Qualité JPEG des aperçus. */
export const PREVIEW_QUALITY = 0.82;
/** Pages de PDF rendues au plus (les premières). */
export const MAX_PREVIEW_PAGES = 12;
/** Bordeaux de la charte (#2f0606) à 5 % : présent sans gêner la lecture (9 % jugé trop présent au pilote). */
export const WATERMARK_COLOR = 'rgba(47, 6, 6, 0.05)';
/** Halo blanc cassé (#f1f1ef) très léger : garde le filigrane perceptible sur les rendus à fond noir. */
export const WATERMARK_HALO = 'rgba(241, 241, 239, 0.045)';
export const WATERMARK_FONT = '"DM Sans Variable", "DM Sans", "Segoe UI", system-ui, sans-serif';

export type PreviewKind = 'image' | 'pdf' | null;

const IMAGE_MIMES = new Set(['image/jpeg', 'image/pjpeg', 'image/png', 'image/webp']);
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp']);

/** Ce que l'on sait montrer : JPEG, PNG, WebP et PDF. Le reste (STEP, vidéo, ZIP, HEIC, TIFF…) part sans aperçu. */
export function previewKind(mime: string, name: string): PreviewKind {
  const ext = (name.toLowerCase().split('.').pop() ?? '').trim();
  const m = mime.toLowerCase();
  if (IMAGE_MIMES.has(m) || (!m.startsWith('image/') && IMAGE_EXTS.has(ext)) || (m === '' && IMAGE_EXTS.has(ext))) return 'image';
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
  return null;
}

/** Taille d'un aperçu : jamais agrandi, plus grand côté ramené à `max`, proportions gardées, au moins 1 px. */
export function previewSize(width: number, height: number, max: number = PREVIEW_MAX_PX): { width: number; height: number; scale: number } {
  const w = Math.max(1, width), h = Math.max(1, height);
  const scale = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)), scale };
}

/** Nombre de pages de PDF à rendre : les premières, MAX_PREVIEW_PAGES au plus. */
export function previewPageCount(numPages: number, max: number = MAX_PREVIEW_PAGES): number {
  if (!Number.isFinite(numPages) || numPages <= 0) return 0;
  return Math.min(Math.floor(numPages), max);
}

/** Texte du filigrane, en capitales : « ACTE·5 · CONFIDENTIEL ». */
export function watermarkLabel(lang: LogeLang): string {
  return watermarkText(lang).toLocaleUpperCase(lang === 'en' ? 'en-GB' : 'fr-FR');
}

export interface WatermarkLayout {
  /** Rotation en radians (négative : la ligne monte vers la droite). */
  angle: number;
  fontSize: number;
  /** Espacement des lettres, en px. */
  letterSpacing: number;
  /** Écart entre deux lignes de filigrane (repère tourné). */
  lineGap: number;
  /** Diagonale de l'image : côté du carré à couvrir dans le repère tourné, centré sur l'image. */
  span: number;
  /** Ordonnées des lignes dans le repère tourné (0 = centre de l'image). */
  rows: number[];
}

const DEG = Math.PI / 180;

/**
 * Disposition du filigrane : lignes parallèles en diagonale (entre 25° et 45°), une passant par le centre, espacées
 * de seize corps de texte (huit avant le pilote : trop dense), sur toute la diagonale pour qu'un recadrage ne
 * l'efface pas.
 */
export function watermarkLayout(width: number, height: number): WatermarkLayout {
  const w = Math.max(1, width), h = Math.max(1, height);
  const span = Math.hypot(w, h);
  const angle = -Math.min(45 * DEG, Math.max(25 * DEG, Math.atan2(h, w)));
  const fontSize = Math.round(Math.min(44, Math.max(9, span * 0.0135)));
  const letterSpacing = Math.round(fontSize * 0.22 * 10) / 10;
  const lineGap = fontSize * 16;
  const half = Math.ceil(span / 2 / lineGap);
  const rows: number[] = [];
  for (let i = -half; i <= half; i++) rows.push(i * lineGap);
  return { angle, fontSize, letterSpacing, lineGap, span, rows };
}

/**
 * Emplacements du texte (début de chaque occurrence, repère tourné) : sur chaque ligne, le texte se répète avec un
 * blanc de vingt corps ; une ligne sur deux est décalée d'une demi-période pour éviter l'effet de colonnes.
 */
export function watermarkPositions(layout: WatermarkLayout, textWidth: number): Array<{ x: number; y: number }> {
  const period = Math.max(1, textWidth) + layout.fontSize * 20;
  const half = layout.span / 2;
  const out: Array<{ x: number; y: number }> = [];
  layout.rows.forEach((y, i) => {
    const shift = (i % 2 === 0 ? 0 : period / 2) - textWidth / 2;
    const first = Math.floor((-half - shift - textWidth) / period);
    for (let k = first; shift + k * period < half; k++) out.push({ x: shift + k * period, y });
  });
  return out;
}

export interface ChunkRange { part: number; start: number; end: number }

/** Découpage de l'original en morceaux (parties numérotées à partir de 1, `end` exclu). Un fichier vide fait une partie. */
export function chunkRanges(size: number, chunkBytes: number = CHUNK_BYTES): ChunkRange[] {
  const step = Math.max(1, Math.min(CHUNK_BYTES, Math.floor(chunkBytes) || CHUNK_BYTES));
  const total = Math.max(0, Math.floor(size));
  if (total === 0) return [{ part: 1, start: 0, end: 0 }];
  const out: ChunkRange[] = [];
  for (let start = 0, part = 1; start < total; start += step, part++) out.push({ part, start, end: Math.min(total, start + step) });
  return out;
}

// ── Navigateur ──────────────────────────────────────────────────────────────────────────────────────

export interface PreviewResult {
  kind: PreviewKind;
  /** Un JPEG par page (une seule pour une image) ; vide si pas d'aperçu. */
  pages: Blob[];
  /** Pages du document (PDF), pour dire « 12 premières pages sur 40 ». */
  totalPages: number;
}

export interface PreviewOptions {
  signal?: AbortSignal;
  onPage?: (done: number, total: number) => void;
}

function newCanvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

function context(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Le navigateur refuse de dessiner l’aperçu (canevas indisponible).');
  return ctx;
}

function release(c: HTMLCanvasElement): void { c.width = 0; c.height = 0; }

function toJpeg(c: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('Export JPEG de l’aperçu impossible.'))), 'image/jpeg', PREVIEW_QUALITY);
  });
}

function checkAbort(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Envoi annulé.', 'AbortError');
}

/** Pose le filigrane sur tout le canevas. */
export async function drawWatermark(ctx: CanvasRenderingContext2D, width: number, height: number, label: string): Promise<void> {
  const layout = watermarkLayout(width, height);
  const font = `600 ${layout.fontSize}px ${WATERMARK_FONT}`;
  try { await document.fonts?.load(font, label); } catch { /* police de secours */ }
  ctx.save();
  ctx.font = font;
  ctx.textBaseline = 'middle';
  // `letterSpacing` du canevas (Chrome, Safari récents) ; sinon espaces fines entre les lettres.
  const spaced = 'letterSpacing' in ctx;
  const text = spaced ? label : label.split('').join(' ');
  if (spaced) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${layout.letterSpacing}px`;
  const textWidth = ctx.measureText(text).width;
  ctx.translate(width / 2, height / 2);
  ctx.rotate(layout.angle);
  const halo = Math.max(1, layout.fontSize * 0.06);
  for (const p of watermarkPositions(layout, textWidth)) {
    ctx.fillStyle = WATERMARK_HALO;
    ctx.fillText(text, p.x, p.y + halo);
    ctx.fillStyle = WATERMARK_COLOR;
    ctx.fillText(text, p.x, p.y);
  }
  ctx.restore();
}

async function decodeImage(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch { /* repli sur <img> */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch {
    URL.revokeObjectURL(url);
    throw new Error('Image illisible : le fichier est peut-être abîmé.');
  }
}

/**
 * Image réduite à PREVIEW_MAX_PX au plus (`max` : 2000 px pour les vues d'un rendu 360°), fond blanc sous les détourés
 * PNG (ou `background`, le fond d'un export KeyShotXR), filigrane (sauf `label` vide), JPEG.
 */
export async function imagePreview(file: Blob, label: string, signal?: AbortSignal, opts: { max?: number; background?: string | null } = {}): Promise<Blob> {
  const img = await decodeImage(file);
  checkAbort(signal);
  const size = previewSize(img.width, img.height, opts.max ?? PREVIEW_MAX_PX);
  const c = newCanvas(size.width, size.height);
  try {
    const ctx = context(c);
    ctx.fillStyle = opts.background || '#ffffff';
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img.source, 0, 0, size.width, size.height);
    if (label) await drawWatermark(ctx, size.width, size.height, label);
    return await toJpeg(c);
  } finally {
    img.close();
    release(c);
  }
}

type PdfJs = typeof import('pdfjs-dist');
let pdfjsPromise: Promise<PdfJs> | null = null;

/** pdf.js et son worker, chargés une seule fois, au premier PDF. */
function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]).then(([lib, worker]) => {
    lib.GlobalWorkerOptions.workerSrc = worker.default;
    return lib;
  }).catch((e: unknown) => {
    pdfjsPromise = null;
    throw new Error('Lecteur PDF indisponible : ' + (e instanceof Error ? e.message : String(e)));
  });
  return pdfjsPromise;
}

/** Premières pages d'un PDF (MAX_PREVIEW_PAGES au plus), chacune à PREVIEW_MAX_PX au plus, filigranées. */
export async function pdfPreviews(file: Blob, label: string, opts: PreviewOptions = {}): Promise<{ pages: Blob[]; totalPages: number }> {
  const pdfjs = await loadPdfJs();
  checkAbort(opts.signal);
  const data = new Uint8Array(await file.arrayBuffer());
  const task = pdfjs.getDocument({ data });
  const abort = () => { void task.destroy(); };
  opts.signal?.addEventListener('abort', abort, { once: true });
  try {
    const doc = await task.promise;
    const count = previewPageCount(doc.numPages);
    const pages: Blob[] = [];
    for (let i = 1; i <= count; i++) {
      checkAbort(opts.signal);
      const page = await doc.getPage(i);
      const base = page.getViewport({ scale: 1 });
      // Une page A4 fait 595 × 842 points : le dessin vectoriel est rendu directement à la taille de l'aperçu
      // (plus grand côté = PREVIEW_MAX_PX), pour un texte net sans jamais dépasser cette taille.
      const scale = PREVIEW_MAX_PX / Math.max(1, base.width, base.height);
      const viewport = page.getViewport({ scale });
      const c = newCanvas(Math.round(viewport.width), Math.round(viewport.height));
      try {
        const ctx = context(c);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        await page.render({ canvas: c, viewport }).promise;
        await drawWatermark(ctx, c.width, c.height, label);
        pages.push(await toJpeg(c));
      } finally {
        release(c);
        page.cleanup();
      }
      opts.onPage?.(i, count);
    }
    return { pages, totalPages: doc.numPages };
  } finally {
    opts.signal?.removeEventListener('abort', abort);
    void task.destroy();
  }
}

/** Aperçus d'un fichier à déposer : image, PDF, ou rien (previewPages 0). */
export async function buildPreviews(file: File, lang: LogeLang, opts: PreviewOptions = {}): Promise<PreviewResult> {
  const kind = previewKind(file.type, file.name);
  const label = watermarkLabel(lang);
  if (kind === 'image') {
    const page = await imagePreview(file, label, opts.signal);
    opts.onPage?.(1, 1);
    return { kind, pages: [page], totalPages: 1 };
  }
  if (kind === 'pdf') {
    const r = await pdfPreviews(file, label, opts);
    return { kind, pages: r.pages, totalPages: r.totalPages };
  }
  return { kind: null, pages: [], totalPages: 0 };
}

/** Couverture du projet : une image réduite, sans filigrane (c'est l'image de présentation choisie par le studio). */
export async function coverPreview(file: File, signal?: AbortSignal): Promise<Blob> {
  if (previewKind(file.type, file.name) !== 'image') throw new Error('La couverture doit être une image (JPEG, PNG ou WebP).');
  return imagePreview(file, '', signal);
}
